import {
  AutoMode,
  Delete as DeleteIcon,
  Error as ErrorIcon,
  Flag as FlagIcon,
  PlayCircle as PlayCircleIcon,
  LocalOffer as TagIcon,
} from "@mui/icons-material";
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useApiMutation } from "../../hooks/use-api-mutation";
import type { RunTestByIdParams } from "../../types";
import type { TestCase } from "./types";

type RunProps = {
  test_ids: string[];
};

interface FloatingActionBarProps {
  selectedTests: TestCase[];
  onDelete: (tests: TestCase[]) => void;
  onPromote: (tests: TestCase[]) => void;
  onUpdatePriority: (tests: TestCase[]) => void;
  onUpdateSeverity: (tests: TestCase[]) => void;
  onUpdateTags: (tests: TestCase[]) => void;
}

export const FloatingActionBar: React.FC<FloatingActionBarProps> = ({
  selectedTests,
  onDelete,
  onPromote,
  onUpdatePriority,
  onUpdateSeverity,
  onUpdateTags,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { mutate: runMultipleTests, isPending: isRunningMultipleTests } = useApiMutation<
    RunProps,
    RunTestByIdParams
  >("POST", `/vibe-qa/run-tests`, {
    onSuccess: () => {
      enqueueSnackbar("Test runs started successfully!", {
        variant: "success",
        anchorOrigin: {
          horizontal: "right",
          vertical: "bottom",
        },
      });
    },
  });

  const handleRun = () => {
    // Extract test IDs from selected tests
    const testIds = selectedTests
      .filter((test) => test.type === "test" && test.test_data?.id)
      .map((test) => test.test_data!.id);

    if (testIds.length > 0) runMultipleTests({ test_ids: testIds });
  };

  const handlePromote = () => {
    onPromote(selectedTests);
  };

  const handleDelete = () => {
    onDelete(selectedTests);
  };

  const handleUpdatePriority = () => {
    onUpdatePriority(selectedTests);
  };

  const handleUpdateSeverity = () => {
    onUpdateSeverity(selectedTests);
  };

  const handleUpdateTags = () => {
    onUpdateTags(selectedTests);
  };

  if (selectedTests.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
      }}
    >
      <Paper
        elevation={8}
        sx={{
          borderRadius: 2,
          backgroundColor: "text.primary",
          color: "white",
          px: 2,
          py: 1,
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ mr: 1, fontSize: 12 }}>
            {selectedTests.length} selected
          </Typography>

          <Tooltip title="Run">
            <IconButton
              size="small"
              disabled={
                isRunningMultipleTests || selectedTests.some((test) => test.type !== "test")
              }
              onClick={handleRun}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
                "&:disabled": {
                  color: "rgba(255, 255, 255, 0.3)",
                },
              }}
            >
              <PlayCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Promote">
            <IconButton
              size="small"
              onClick={handlePromote}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <AutoMode fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Update Priority">
            <IconButton
              size="small"
              onClick={handleUpdatePriority}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <FlagIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Update Severity">
            <IconButton
              size="small"
              onClick={handleUpdateSeverity}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <ErrorIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Add Tags">
            <IconButton
              size="small"
              onClick={handleUpdateTags}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <TagIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={handleDelete}
              sx={{
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>
    </Box>
  );
};
